import React from "react";
import personal from "assets/images/svg/personal_info_bg.svg";
import Img from "assets/images/svg/account_menu.svg";
import { Link } from "react-router-dom";
import { useModal } from "hooks";
import PictureModal from "components/picture-modal/picture-modal";
import Camera from "assets/images/svg/camera_blue.svg";
import { Button, Form, InputField, SelectField } from "components/form";
import { useQuery, useQueryClient } from "react-query";
import { InfoService } from "pages/informations/info.service";
import { client } from "helpers/client";
import { CustomerData } from "pages/account/account.service";
import { Spinner } from "react-bootstrap";
import ServerImage from "components/server-image/server-image";
import { json } from "helpers/data";
import ErrorMessage from "components/error/error";
import { urlPattern } from "helpers/patterns";
import { toast } from "react-toastify";
import { Trans, useTranslation } from "react-i18next";
import CanLoadGuard, { CanLoadPage } from "components/private-route/can-load.guard";
import {useAuthContext} from "store/auth";

const ProfileInfoPage = () => {
  const { t } = useTranslation("profile");
  const [{user} ] = useAuthContext();
  const queryClient = useQueryClient();
  const [isPictureModalShown, showPictureModal, closePictureModal] = useModal(false);
  const { data: companySizes = [] } = useQuery("company-sizes", InfoService.getCompanySizes);
  const { data: industryFields = [] } = useQuery("industry-fields", InfoService.getIndustryFields);
  const {
    data: customerData,
    isSuccess,
    isError,
    isLoading
  } = useQuery("customer-data", () =>
    client<CustomerData>("/customer/getCurrentCustomer").then((c) => {
      if (c.error) return Promise.reject();
      return c.response;
    })
  );

  async function handleCustomerImageUpload(image: Blob, imageName: string) {
    const formData = new FormData();
    formData.append("file", new File([image], imageName, { type: image.type }));

    const { response, error } = await client("/customer/uploadImage", {
      data: formData,
      headers: { "Content-Type": "multipart/form-data" }
    });
    if (response) {
      closePictureModal();
      toast.success(t("common:form.added"));
      return queryClient.invalidateQueries("customer-data");
    }
    if (error) return;
  }

  async function handleUpdateAccount(data: any) {
    const customerDataDto = {
      name: data.fullName,
      postalCode: data.postalCode,
      companyName: data.companyName,
      taxRegistrationNumber: data.taxNumber,
      industryId: data.industryField,
      companySizeId: data.companySize,
      companyWebsite: data.domain,
      city: data.city,
      fullAddress: data.fullAddress
    };

    const { response } = await client("/customer/update", {
      data: customerDataDto
    });
    if (response) {
      toast.success(t("common:form.updated"));
      return queryClient.invalidateQueries("customer-data");
    }
  }

  return (
    <CanLoadPage>
      <div className="myAccount">
        <div className="row">
          <div className="col-lg-4 col-md-6">
            <div className="picture-input">
              <div className="details">
                <ServerImage src={customerData?.logo?.fullPath ?? ""} alt="customer-image" />
                <div className="info">
                  <h3>{customerData?.name}</h3>
                  <p>{customerData?.email}</p>
                  <p>{customerData?.phone}</p>
                </div>
              </div>
              <CanLoadGuard>
                <button className="upload" onClick={showPictureModal}>
                  <img src={Camera} alt="Camera" />
                </button>
              </CanLoadGuard>
            </div>
            {isLoading && <Spinner animation="grow" variant="primary" />}
            {isError && <ErrorMessage />}
            {isSuccess && (
              <Form
                onSubmit={handleUpdateAccount}
                defaultValues={{
                  fullName: customerData!.name,
                  domain: customerData!.companyWebsite,
                  taxNumber: customerData!.taxRegistrationNumber,
                  companyName: customerData!.companyName,
                  industryField: customerData!.industry?.id ?? "",
                  companySize: customerData!.companySize?.id ?? "",
                  country: json(customerData!.country ?? ""),
                  city: customerData!.city,
                  postalCode: customerData!.postalCode,
                  fullAddress: customerData!.fullAddress
                }}
              >
                {({ formState: { isDirty } }) => {
                  return (
                    <>
                      <InputField id="fullName" type="text" label={t("full-name")} rules={{ required: "errors:required" }} />
                      <InputField
                        id="domain"
                        type="domain"
                        label={t("company-website")}
                        rules={{
                          required: "errors:required",
                          pattern: {
                            value: urlPattern,
                            message: "errors:invalidFormat"
                          }
                        }}
                      />

                      <div className="row">
                        <div className="col-lg-6">
                          <InputField
                            id="companyName"
                            type="text"
                            label={t("company-name")}
                            rules={{ required: "errors:required" }}
                          />
                        </div>
                        <div className="col-lg-6">
                          <InputField
                            id="taxNumber"
                            type="text"
                            label={t("tax-reg-number")}
                            rules={{ required: "errors:required" }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-6">
                          <SelectField
                            id="industryField"
                            labelField="name"
                            disabled={user.isSuspended}
                            type="select"
                            label={t("industry")}
                            options={industryFields}
                            rules={{ required: "errors:required" }}
                          />
                        </div>
                        <div className="col-md-6">
                          <SelectField
                            id="companySize"
                            type="select"
                            disabled={user.isSuspended}
                            label={t("company-size")}
                            labelField="name"
                            options={companySizes}
                            rules={{ required: "errors:required" }}
                          />
                        </div>
                      </div>
                      <InputField id="country" type="text" label={t("country")} readonly />
                      <p className="info">
                        <Trans t={t} i18nKey="contact-customer-support">
                          please contact <Link to="/contact-us">customer support</Link> to update your profile
                        </Trans>
                        {/*{t('contact-customer-support',{customer_support:<a href="/">{t('customer-support')}</a>})}*/}
                      </p>
                      <div className="row">
                        <div className="col-md-6">
                          <InputField id="city" type="text" label={t("city")} rules={{ required: "errors:required" }} />
                        </div>
                        <div className="col-md-6">
                          <InputField
                            id="postalCode"
                            type="text"
                            label={t("postal-code")}
                            rules={{ required: "errors:required" }}
                          />
                        </div>
                      </div>
                      <InputField
                        id="fullAddress"
                        type="text"
                        label={t("full-address")}
                        rules={{ required: "errors:required" }}
                      />
                      <CanLoadGuard>
                        <Button disabled={!isDirty} className="btn-blue">
                          {t("update-changes")}
                        </Button>
                      </CanLoadGuard>
                    </>
                  );
                }}
              </Form>
            )}
          </div>
          <div className="col-lg-8 col-md-6">
            <img className="img-fluid background" src={personal} alt="" />
          </div>
        </div>
        <PictureModal
          src={customerData?.logo?.fullPath ?? Img}
          show={isPictureModalShown}
          hide={closePictureModal}
          title={t("upload-profile-photo")}
          onSubmit={handleCustomerImageUpload}
        />
      </div>
    </CanLoadPage>
  );
};

export default ProfileInfoPage;
